// components/Footer.tsx
"use client";

import Link from 'next/link';
import { useState, useEffect } from 'react';
import {
  FaArrowUp,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedinIn,
} from 'react-icons/fa';

export default function Footer() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Service Categories
  const serviceCategories = [
    { name: 'Household Shifting', href: '/services/household' },
    { name: 'Office Relocation', href: '/services/office' },
    { name: 'Packers and Movers', href: '/services/packers-movers' },
    { name: 'Car Transport', href: '/services/car' },
    { name: 'Bike Transport', href: '/services/bike' },
    { name: 'Pet Shifting', href: '/services/pet' },
    { name: 'International Relocation', href: '/services/international' },
    { name: 'Warehouse Service', href: '/services/warehouse' },
    { name: 'Loading and Unloading', href: '/services/loading-unloading' },
    { name: 'Commercial Relocation', href: '/services/commercial' },
    { name: 'Local Relocation', href: '/services/local' },
    { name: 'Goods Insurance', href: '/services/insurance' },
  ];

  // Top 30 Cities
  const topCities = [
    'Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Hyderabad',
    'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow',
    'Noida', 'Gurgaon', 'Faridabad', 'Ghaziabad', 'Surat',
    'Nagpur', 'Indore', 'Bhopal', 'Patna', 'Visakhapatnam',
    'Coimbatore', 'Madurai', 'Mysore', 'Chandigarh', 'Agra',
    'Vadodara', 'Ludhiana', 'Nashik', 'Ranchi', 'Kochi',
  ];

  // Popular Cities (for columns)
  const popularCities = [
    { name: 'Delhi', href: '/packers-movers-in-delhi' },
    { name: 'Mumbai', href: '/packers-movers-in-mumbai' },
    { name: 'Bangalore', href: '/packers-movers-in-bangalore' },
    { name: 'Chennai', href: '/packers-movers-in-chennai' },
    { name: 'Hyderabad', href: '/packers-movers-in-hyderabad' },
    { name: 'Pune', href: '/packers-movers-in-pune' },
    { name: 'Kolkata', href: '/packers-movers-in-kolkata' },
    { name: 'Ahmedabad', href: '/packers-movers-in-ahmedabad' },
    { name: 'Jaipur', href: '/packers-movers-in-jaipur' },
    { name: 'Lucknow', href: '/packers-movers-in-lucknow' },
    { name: 'Noida', href: '/packers-movers-in-noida' },
    { name: 'Gurgaon', href: '/packers-movers-in-gurgaon' },
    { name: 'Faridabad', href: '/packers-movers-in-faridabad' },
    { name: 'Ghaziabad', href: '/packers-movers-in-ghaziabad' },
    { name: 'Surat', href: '/packers-movers-in-surat' },
    { name: 'Nagpur', href: '/packers-movers-in-nagpur' },
    { name: 'Indore', href: '/packers-movers-in-indore' },
    { name: 'Bhopal', href: '/packers-movers-in-bhopal' },
    { name: 'Patna', href: '/packers-movers-in-patna' },
    { name: 'Visakhapatnam', href: '/packers-movers-in-visakhapatnam' },
    { name: 'Coimbatore', href: '/packers-movers-in-coimbatore' },
    { name: 'Madurai', href: '/packers-movers-in-madurai' },
    { name: 'Mysore', href: '/packers-movers-in-mysore' },
    { name: 'Chandigarh', href: '/packers-movers-in-chandigarh' },
    { name: 'Agra', href: '/packers-movers-in-agra' },
    { name: 'Vadodara', href: '/packers-movers-in-vadodara' },
    { name: 'Ludhiana', href: '/packers-movers-in-ludhiana' },
    { name: 'Nashik', href: '/packers-movers-in-nashik' },
    { name: 'Ranchi', href: '/packers-movers-in-ranchi' },
    { name: 'Kochi', href: '/packers-movers-in-kochi' },
  ];

  // Quick Links
  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Franchise', href: '/franchise' },
    { name: 'Contact', href: '/contact' },
    { name: 'Blog', href: '/blog' },
    { name: 'FAQs', href: '/faqs' },
    { name: 'Sitemap', href: '/sitemap' },
  ];

  // Resources
  const resources = [
    { name: 'Track Order', href: '/tracking' },
    { name: 'Online Payment', href: '/payment' },
    { name: 'Moving Checklist', href: '/moving-checklist' },
    { name: 'Shifting Guide', href: '/shifting-guide' },
    { name: 'Packing Tips', href: '/packing-tips' },
    { name: 'Insurance Guide', href: '/insurance-guide' },
    { name: 'Why Movezze', href: '/why-movezze' },
    { name: 'Customer Reviews', href: '/reviews' },
    { name: 'Awards', href: '/awards' },
    { name: 'Careers', href: '/careers' },
  ];

  // Legal
  const legalPages = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Cancellation & Refund', href: '/cancellation-refund' },
    { name: 'Disclaimer', href: '/disclaimer' },
    { name: 'Cookie Policy', href: '/cookie-policy' },
    { name: 'Shipping Policy', href: '/shipping-policy' },
    { name: 'Franchise Policy', href: '/franchise-policy' },
  ];

  return (
    <footer className="bg-white border-t border-gray-200 pt-16 pb-6">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-8 pb-12 border-b border-gray-200">

          {/* Column 1: Brand */}
          <div className="col-span-1 sm:col-span-2 md:col-span-1">
            <Link href="/" className="inline-block mb-4">
              <img
                src="/images/Movezze-Logistics-Private-Limited-Logo.svg"
                alt="Movezze Logistics Private Limited"
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-sm text-[#343434] leading-relaxed">
              India's trusted packers and movers network. Household shifting, bike transport, car carrier, pet shifting, and international relocation services across 100+ cities.
            </p>
            <div className="flex gap-3 mt-4">
              <a href="#" className="text-[#343434] hover:text-[#f62b1e] transition-colors">
                <FaFacebookF size={18} />
              </a>
              <a href="#" className="text-[#343434] hover:text-[#f62b1e] transition-colors">
                <FaTwitter size={18} />
              </a>
              <a href="#" className="text-[#343434] hover:text-[#f62b1e] transition-colors">
                <FaInstagram size={18} />
              </a>
              <a href="#" className="text-[#343434] hover:text-[#f62b1e] transition-colors">
                <FaYoutube size={18} />
              </a>
              <a href="#" className="text-[#343434] hover:text-[#f62b1e] transition-colors">
                <FaLinkedinIn size={18} />
              </a>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-yellow-400 text-sm">★★★★★</span>
                <span className="text-sm font-semibold text-[#343434]">4.9/5</span>
                <span className="text-xs text-[#343434]">– 2,500+ Reviews</span>
              </div>
            </div>
          </div>

          {/* Column 2: Services */}
          <div>
            <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">Our Services</h4>
            <ul className="space-y-2.5">
              {serviceCategories.map((service, index) => (
                <li key={index}>
                  <Link
                    href={service.href}
                    className="text-sm text-[#343434] hover:text-[#f62b1e] transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Popular Cities (Part 1) */}
          <div>
            <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">Popular Cities</h4>
            <ul className="space-y-2.5">
              {popularCities.slice(0, 12).map((city, index) => (
                <li key={index}>
                  <Link
                    href={city.href}
                    className="text-sm text-[#343434] hover:text-[#f62b1e] transition-colors"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Popular Cities (Part 2) */}
          <div>
            <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">More Cities</h4>
            <ul className="space-y-2.5">
              {popularCities.slice(12, 24).map((city, index) => (
                <li key={index}>
                  <Link
                    href={city.href}
                    className="text-sm text-[#343434] hover:text-[#f62b1e] transition-colors"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <Link
                href="/all-cities"
                className="text-xs text-[#343434] font-medium hover:text-[#f62b1e] transition-colors"
              >
                View all cities →
              </Link>
            </div>
          </div>

          {/* Column 5: Quick Links + Resources */}
          <div>
            <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-sm text-[#343434] hover:text-[#f62b1e] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">Resources</h4>
              <ul className="space-y-2.5">
                {resources.slice(0, 6).map((resource, index) => (
                  <li key={index}>
                    <Link
                      href={resource.href}
                      className="text-sm text-[#343434] hover:text-[#f62b1e] transition-colors"
                    >
                      {resource.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 6: Legal + Contact */}
          <div>
            <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-2.5">
              {legalPages.map((page, index) => (
                <li key={index}>
                  <Link
                    href={page.href}
                    className="text-sm text-[#343434] hover:text-[#f62b1e] transition-colors"
                  >
                    {page.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-[#292929] font-semibold text-sm uppercase tracking-wider mb-4">Get In Touch</h4>
              <ul className="space-y-2 text-sm text-[#343434]">
                <li>
                  <a href="tel:+918130880900" className="hover:text-[#f62b1e] transition-colors">
                    +91 8130 880 900
                  </a>
                </li>
                <li>
                  <a href="mailto:info@movezze.com" className="hover:text-[#f62b1e] transition-colors">
                    info@movezze.com
                  </a>
                </li>
                <li className="text-[#343434]">Delhi NCR, India</li>
                <li className="text-[#343434]">Mon–Sat: 9:00 AM – 8:00 PM</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ===== CITIES WE SERVE – 30 Cities ===== */}
        <div className="pt-8 pb-6 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-[#292929] uppercase tracking-wider mb-4">
            Cities We Serve
          </h3>
          <div className="flex flex-wrap gap-2">
            {topCities.map((city) => (
              <Link
                key={city}
                href={`/packers-movers-in-${city.toLowerCase()}`}
                className="text-xs text-[#343434] hover:text-[#f62b1e] transition-colors px-2 py-1 border border-gray-200 rounded-full hover:border-[#f62b1e]"
              >
                {city}
              </Link>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Link
              href="/all-cities"
              className="text-sm text-[#343434] font-medium hover:text-[#f62b1e] transition-colors"
            >
              View all cities we serve →
            </Link>
          </div>
        </div>

        {/* ===== DOWNLOAD APP SECTION ===== */}
        <div className="flex flex-wrap items-center justify-between gap-6 py-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.movezze"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="https://res.cloudinary.com/myhq/image/upload/web/ssr/common/assets/play-store-badge.svg"
                  alt="Get it on Google Play"
                  className="h-10 w-auto"
                />
              </a>
              <a
                href="https://apps.apple.com/in/app/movezze"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="https://res.cloudinary.com/myhq/image/upload/web/ssr/common/assets/Appstore.svg"
                  alt="Download on the App Store"
                  className="h-10 w-auto"
                />
              </a>
            </div>
          </div>

          {/* Keep in touch + Social icons */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-[#343434]">Keep in touch</span>
            <div className="flex items-center gap-2">
              <a
                href="https://www.facebook.com/movezze"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-[#f62b1e] rounded-full flex items-center justify-center text-[#343434] hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <FaFacebookF size={14} />
              </a>
              <a
                href="https://twitter.com/movezze"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-[#f62b1e] rounded-full flex items-center justify-center text-[#343434] hover:text-white transition-colors"
                aria-label="Twitter"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 18 18">
                  <path d="M15.117 15.458a.56.56 0 0 1-.492.292H11.25a.56.56 0 0 1-.475-.26l-2.847-4.475-4.137 4.55a.563.563 0 0 1-.832-.756l4.343-4.781L2.9 3.115a.562.562 0 0 1 .475-.865H6.75a.56.56 0 0 1 .475.26l2.847 4.475 4.137-4.55a.563.563 0 0 1 .832.756l-4.343 4.778 4.402 6.917a.56.56 0 0 1 .017.572z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/movezze/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-[#f62b1e] rounded-full flex items-center justify-center text-[#343434] hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <FaInstagram size={14} />
              </a>
              <a
                href="https://www.linkedin.com/company/movezze"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-[#f62b1e] rounded-full flex items-center justify-center text-[#343434] hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn size={14} />
              </a>
              <a
                href="https://www.youtube.com/@movezze"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-[#f62b1e] rounded-full flex items-center justify-center text-[#343434] hover:text-white transition-colors"
                aria-label="YouTube"
              >
                <FaYoutube size={14} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6">
          <div className="flex flex-wrap items-center gap-4 text-xs text-[#343434]">
            <span>
              &copy; {new Date().getFullYear()} Movezze Logistics Private Limited. All rights reserved.
            </span>
            <span className="text-[#343434] hidden md:inline">|</span>
            <Link href="/privacy-policy" className="hover:text-[#f62b1e] transition-colors">
              Privacy
            </Link>
            <Link href="/terms" className="hover:text-[#f62b1e] transition-colors">
              Terms
            </Link>
            <Link href="/sitemap" className="hover:text-[#f62b1e] transition-colors">
              Sitemap
            </Link>
            <span className="text-[#343434] hidden md:inline">|</span>
            <span className="text-[#343434]">CIN: UXXXXXX2026PLCXXXXXX</span>
          </div>
          <div className="text-xs text-[#343434]">
            <span className="inline-flex items-center gap-1">
              Made with ❤️ in India
            </span>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-[#f62b1e] text-white p-3 rounded-full shadow-lg hover:bg-[#df261a] transition-all duration-300 z-50 hover:scale-105"
          aria-label="Back to top"
        >
          <FaArrowUp className="text-lg" />
        </button>
      )}
    </footer>
  );
}